import React, { useContext } from 'react'
import { connect } from 'react-redux'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import { Card, CardContent, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { CONTENT_TYPES } from '@scoir/college-content'
import CollegeMetricsContext from '../CollegeMetrics/CollegeMetricsProvider/Context'
import MetricSection from './MetricSection'
import ClassYearMetric from './ClassYearMetric'
import PercentageMetric from './PercentageMetricCard'
import InterestedStudentsByMonthCard from './InterestedStudentsByMonthCard'
import ProspectiveStudentsByLocationCard from './ProspectiveStudentsByLocationCard'
import { ErrorBoundary as StandardErrorBoundary } from '@scoir/error-reporter'
import ErrorMessage from './ErrorMessage'
import NumericMetricCard from './NumericMetricCard'
import CompetitorTable from './CompetitorTable'
import MessageTable from './Table/MessageTable'
import ContentTable from './Table/ContentTable'
import ConversionByYearCard from './ConversionByYearCard'

const ErrorBoundary = ({ children }) => (
  <StandardErrorBoundary
    errorComponent={() => (
      <Card
        p={2}
        elevation={0}
      >
        <CardContent>
          <ErrorMessage />
        </CardContent>
      </Card>
    )}
  >
    {children}
  </StandardErrorBoundary>
)
ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
}

const useStyles = makeStyles((theme) => ({
  pageTitle: {
    marginBottom: theme.spacing(2.5),
  },
  gridItemBottom: {
    marginBottom: theme.spacing(8.5),
  },
}))

export const Metrics = ({ collegeName }) => {
  const { error, loading, collegeMetrics } = useContext(CollegeMetricsContext)
  const {
    scoir_audience_total,
    engaged_students_totals,
    reached_students_total,
    student_impressions_total,
    registered_with_parent,
    attending_title_1_hs,
    first_gen_student,
    reached_academic_focus,
    reached_interest,
    senior_grad_year,
    reached_by_grad_year,
    engaged_students_by_month,
    engaged_registered_with_parent,
    engaged_first_gen_student,
    engaged_attending_title_1_hs,
    engaged_academic_focus,
    engaged_interest,
    competitors_adjusted,
    competitors,
    student_conversions,
  } = collegeMetrics
  const styles = useStyles()

  return (
    <Grid
      container
      spacing={3}
    >
      <Grid
        item
        xs={12}
        className={styles.pageTitle}
      >
        <Typography
          component="h5"
          variant="h5"
          align="center"
        >
          {`Welcome Back, ${collegeName}`}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          align="center"
        >
          Get to know the <strong>{(scoir_audience_total || 0).toLocaleString()}</strong> Students active on Scoir
          today.
        </Typography>
      </Grid>
      <MetricSection
        title="Interested Students"
        tooltip='Your Interested Students are made up of students who add your school to their "My Colleges" list in the Following, Applying, or Applied status.'
        metric={get(engaged_students_totals, 'engagement_all_stages', 0)}
        metricPrevMonth={get(engaged_students_totals, 'engagement_all_stages_30_days_ago', 0)}
      >
        <Grid
          item
          xs={12}
          md={7}
        >
          <ErrorBoundary>
            <InterestedStudentsByMonthCard
              data={engaged_students_by_month || []}
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          md={5}
        >
          <ErrorBoundary>
            <ConversionByYearCard
              seniorGradYear={senior_grad_year}
              data={student_conversions}
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <ErrorBoundary>
            <PercentageMetric
              title="Students with Registered Parent/Guardian"
              subheader="Interested Students"
              metric={get(engaged_registered_with_parent, 'has_registered_parent_pct_college', 0)}
              subMetric={get(engaged_registered_with_parent, 'has_registered_parent_pct_scoir', 0)}
              subMetricLabel="have Registered Parent/Guardian"
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <ErrorBoundary>
            <PercentageMetric
              title="Students Attending a Title 1 School"
              subheader="Interested Students"
              metric={get(engaged_attending_title_1_hs, 'is_title_1_pct_college', 0)}
              subMetric={get(engaged_attending_title_1_hs, 'is_title_1_pct_scoir', 0)}
              subMetricLabel="attend a Title 1 School"
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <ErrorBoundary>
            <PercentageMetric
              title="First Generation Students"
              subheader="Interested Students"
              metric={get(engaged_first_gen_student, 'is_first_gen_pct_college', 0)}
              subMetric={get(engaged_first_gen_student, 'is_first_gen_pct_scoir', 0)}
              subMetricLabel="are First Generation"
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <ErrorBoundary>
            <MessageTable
              data={{
                all: get(engaged_interest, 'all_engaged_interests', []),
                following: get(engaged_interest, 'following_interests', []),
                applying: get(engaged_interest, 'applying_interests', []),
                applied: get(engaged_interest, 'applied_interests', []),
              }}
              error={error}
              loading={loading}
              nameLabel="Interest"
              tableName="Top Interests"
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <ErrorBoundary>
            <MessageTable
              data={{
                all: get(engaged_academic_focus, 'all_engaged_academic_focus', []),
                following: get(engaged_academic_focus, 'following_academic_focus', []),
                applying: get(engaged_academic_focus, 'applying_academic_focus', []),
                applied: get(engaged_academic_focus, 'applied_academic_focus', []),
              }}
              error={error}
              loading={loading}
              nameLabel="Academic Focus"
              tableName="Top Academic Focuses"
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          className={styles.gridItemBottom}
        >
          <ErrorBoundary>
            <ProspectiveStudentsByLocationCard />
          </ErrorBoundary>
        </Grid>
      </MetricSection>
      <MetricSection
        title="Reach"
        tooltip="Your Reach is the total number of unique students who have seen your school at least once on Scoir. This includes search results, profile page views, and student dashboard recommendations."
        metric={get(reached_students_total, 'reached_total', 0)}
        metricPrevMonth={get(reached_students_total, 'reached_total_30_days_ago', 0)}
      >
        <Grid
          item
          xs={12}
          sm={4}
        >
          <ErrorBoundary>
            <NumericMetricCard
              title="Impressions"
              tooltip="Your Impressions are the total number of times your school was seen on Scoir by students."
              metric={get(student_impressions_total, 'impressions_total', 0)}
              metricPrevMonth={get(student_impressions_total, 'impressions_total_30_days_ago', 0)}
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          sm={8}
        >
          <ErrorBoundary>
            <ClassYearMetric
              title="Reach by Class Year"
              seniorGradYear={senior_grad_year || 0}
              total={get(reached_students_total, 'reached_total', 0)}
              gradYearCounts={reached_by_grad_year}
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <ErrorBoundary>
            <PercentageMetric
              title="Students with Registered Parent/Guardian"
              subheader="Reached Students"
              metric={get(registered_with_parent, 'has_registered_parent_pct_college', 0)}
              subMetric={get(registered_with_parent, 'has_registered_parent_pct_scoir', 0)}
              subMetricLabel="have Registered Parent/Guardian"
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <ErrorBoundary>
            <PercentageMetric
              title="Students Attending a Title 1 School"
              subheader="Reached Students"
              metric={get(attending_title_1_hs, 'is_title_1_pct_college', 0)}
              subMetric={get(attending_title_1_hs, 'is_title_1_pct_scoir', 0)}
              subMetricLabel="attend a Title 1 School"
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <ErrorBoundary>
            <PercentageMetric
              title="First Generation Students"
              subheader="Reached Students"
              metric={get(first_gen_student, 'is_first_gen_pct_college', 0)}
              subMetric={get(first_gen_student, 'is_first_gen_pct_scoir', 0)}
              subMetricLabel="are First Generation"
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <ErrorBoundary>
            <ContentTable
              contentType={{
                label: 'Tags',
                context: CONTENT_TYPES.STUDENT_LIFE,
              }}
              data={{
                all: get(reached_interest, 'audience_interests', []),
                reached: get(reached_interest, 'reached_interests', []),
                not_reached: get(reached_interest, 'not_reached_interests', []),
              }}
              error={error}
              loading={loading}
              nameLabel="Interest"
              tableName="Top Interests"
            />
          </ErrorBoundary>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          className={styles.gridItemBottom}
        >
          <ErrorBoundary>
            <ContentTable
              contentType={{
                label: 'AcademicFocus',
                context: CONTENT_TYPES.ACADEMICS,
              }}
              data={{
                all: get(reached_academic_focus, 'audience_academic_focus', []),
                reached: get(reached_academic_focus, 'reached_academic_focus', []),
                not_reached: get(reached_academic_focus, 'not_reached_academic_focus', []),
              }}
              error={error}
              loading={loading}
              nameLabel="Academic Focus"
              tableName="Top Academic Focuses"
            />
          </ErrorBoundary>
        </Grid>
      </MetricSection>
      <MetricSection
        title="Competitive Insights"
        tooltip="Top competitors are colleges that share the most interested students in common with you. The Adjusted Ranking is normalized for schools similar in size to you."
      >
        <Grid
          item
          xs={12}
        >
          <ErrorBoundary>
            <CompetitorTable
              competitors_adjusted={competitors_adjusted}
              competitors={competitors}
              loading={loading}
              error={error}
            />
          </ErrorBoundary>
        </Grid>
      </MetricSection>
    </Grid>
  )
}

Metrics.propTypes = {
  collegeName: PropTypes.string.isRequired,
}

export default connect((store) => ({
  collegeName: get(store.college.record, 'Name'),
}))(Metrics)
